import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  TextField,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  getTournamentPlayers,
  getRoundsMatches,
  saveRoundsMatches,
} from '../../../services/tournament.service';
import { selectMenuProps, compactInputSx } from '../../../styles/wizardStyles';
import { parseMatchResult, serializeMatchResult } from '../../../utils/matchResult.utils';
import RoundsPanel from '../../../components/wizard/RoundsPanel';
import MatchResultSelect from '../../../components/wizard/MatchResultSelect';
import StepNavButtons from '../../../components/wizard/StepNavButtons';



export default function StepNormalRoundsMatches({
  torId,
  onNext,
  onBack,
  setAlertInfo,
  nroRondas,
  onUpdateTournamentRondas,
}) {
  const [enrolledPlayers, setEnrolledPlayers] = useState([]);
  const [rounds, setRounds] = useState([{ ron_id: 1, ron_numero: 1, ron_fecha: '' }]);
  const [matches, setMatches] = useState([]);
  const [initialRounds, setInitialRounds] = useState([]);
  const [initialMatches, setInitialMatches] = useState([]);
  const [activeRoundId, setActiveRoundId] = useState(1);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  // Form states
  const [selectedWhite, setSelectedWhite] = useState('');
  const [selectedBlack, setSelectedBlack] = useState('');
  const [selectedResult, setSelectedResult] = useState('');
  const [selectedByeValue, setSelectedByeValue] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setDataLoading(true);
      try {
        const playersData = await getTournamentPlayers(torId);
        const sortedPlayers = (playersData || []).sort((a, b) =>
          a.apellido_nombre.localeCompare(b.apellido_nombre)
        );
        setEnrolledPlayers(sortedPlayers);

        const data = await getRoundsMatches(torId);
        if (data && data.rondas && data.rondas.length > 0) {
          const formattedRounds = data.rondas.map((r) => ({
            ron_id: r.ron_id,
            ron_numero: r.ron_numero,
            ron_fecha: r.ron_fecha ? r.ron_fecha.split('T')[0] : '',
          }));
          setRounds(formattedRounds);

          const formattedMatches = data.partidas.map((p, idx) => {
            const { result, byeValue, isBye } = parseMatchResult(p);

            return {
              id: idx + 1,
              par_ron_id: p.par_ron_id,
              jug_blancas: p.par_jug_blancas,
              jug_negras: isBye ? 'BYE' : p.par_jug_negras,
              result: isBye ? null : result,
              byeValue: isBye ? byeValue : null,
            };
          });
          setMatches(formattedMatches);
          setInitialRounds(JSON.parse(JSON.stringify(formattedRounds)));
          setInitialMatches(JSON.parse(JSON.stringify(formattedMatches)));
          setActiveRoundId(formattedRounds[0].ron_id);
        } else {
          const defaultRounds = [{ ron_id: 1, ron_numero: 1, ron_fecha: '' }];
          setRounds(defaultRounds);
          setMatches([]);
          setInitialRounds(JSON.parse(JSON.stringify(defaultRounds)));
          setInitialMatches([]);
          setActiveRoundId(1);
        }
      } catch (error) {
        console.error('Error fetching rounds and matches:', error);
        setAlertInfo({
          open: true,
          message: 'Error al obtener los datos de rondas y partidas.',
          severity: 'error',
        });
      } finally {
        setDataLoading(false);
      }
    };
    fetchData();
  }, [torId, setAlertInfo]);

  const handleAddRound = () => {
    const nextRoundNum = rounds.length + 1;
    if (nextRoundNum > nroRondas) {
      const confirmModify = window.confirm(
        `La ronda agregada excede el número de rondas configurado para el torneo (${nroRondas}). ¿Deseas continuar y modificar el total de rondas del torneo?`
      );
      if (!confirmModify) return;

      onUpdateTournamentRondas(nextRoundNum);
    }

    const newRound = {
      ron_id: nextRoundNum,
      ron_numero: nextRoundNum,
      ron_fecha: '',
    };
    setRounds([...rounds, newRound]);
    setActiveRoundId(newRound.ron_id);
  };

  const handleRoundDateChange = (ronId, date) => {
    setRounds((prev) =>
      prev.map((r) => (r.ron_id === ronId ? { ...r, ron_fecha: date } : r))
    );
  };

  const handleAddMatch = () => {
    if (!selectedWhite) {
      setAlertInfo({ open: true, message: 'Debes seleccionar el jugador de Blancas.', severity: 'warning' });
      return;
    }
    if (!selectedBlack) {
      setAlertInfo({ open: true, message: 'Debes seleccionar el jugador de Negras o BYE.', severity: 'warning' });
      return;
    }

    const isBye = selectedBlack === 'BYE';
    if (!isBye && selectedWhite === selectedBlack) {
      setAlertInfo({ open: true, message: 'El jugador de Blancas y Negras no pueden ser la misma persona.', severity: 'warning' });
      return;
    }

    if (isBye && !selectedByeValue) {
      setAlertInfo({ open: true, message: 'Debes seleccionar el valor del BYE.', severity: 'warning' });
      return;
    }
    if (!isBye && !selectedResult) {
      setAlertInfo({ open: true, message: 'Debes seleccionar el resultado de la partida.', severity: 'warning' });
      return;
    }

    // Validation: check that neither player has already played in this round
    const whiteAlreadyPlayed = matches.some(
      (m) =>
        m.par_ron_id === activeRoundId &&
        (m.jug_blancas === selectedWhite || m.jug_negras === selectedWhite)
    );
    if (whiteAlreadyPlayed) {
      const player = enrolledPlayers.find((p) => p.jug_id === selectedWhite);
      setAlertInfo({
        open: true,
        message: `El jugador ${player ? player.apellido_nombre : ''} ya tiene una partida/BYE asignada en esta ronda.`,
        severity: 'warning',
      });
      return;
    }

    if (!isBye) {
      const blackAlreadyPlayed = matches.some(
        (m) =>
          m.par_ron_id === activeRoundId &&
          (m.jug_blancas === selectedBlack || m.jug_negras === selectedBlack)
      );
      if (blackAlreadyPlayed) {
        const player = enrolledPlayers.find((p) => p.jug_id === selectedBlack);
        setAlertInfo({
          open: true,
          message: `El jugador ${player ? player.apellido_nombre : ''} ya tiene una partida asignada en esta ronda.`,
          severity: 'warning',
        });
        return;
      }
    }

    const newMatch = {
      id: Date.now(),
      par_ron_id: activeRoundId,
      jug_blancas: selectedWhite,
      jug_negras: selectedBlack,
      result: isBye ? null : selectedResult,
      byeValue: isBye ? selectedByeValue : null,
    };

    setMatches([...matches, newMatch]);

    // Reset match form
    setSelectedWhite('');
    setSelectedBlack('');
    setSelectedResult('');
    setSelectedByeValue('');
  };

  const handleDeleteMatch = (matchId) => {
    setMatches((prev) => prev.filter((m) => m.id !== matchId));
  };

  const getPlayerName = (jugId) => {
    if (jugId === 'BYE') return 'BYE';
    const player = enrolledPlayers.find((p) => p.jug_id === jugId);
    return player ? player.apellido_nombre : `Jugador ID: ${jugId}`;
  };

  const handleSaveAndNext = async () => {
    const roundsChanged = !initialRounds || rounds.length !== initialRounds.length || rounds.some((r, idx) => {
      const initR = initialRounds[idx];
      return !initR || r.ron_id !== initR.ron_id || r.ron_numero !== initR.ron_numero || (r.ron_fecha || '') !== (initR.ron_fecha || '');
    });

    const matchesChanged = !initialMatches || matches.length !== initialMatches.length || matches.some((m, idx) => {
      const initM = initialMatches[idx];
      return !initM ||
        m.par_ron_id !== initM.par_ron_id ||
        m.jug_blancas !== initM.jug_blancas ||
        m.jug_negras !== initM.jug_negras ||
        m.result !== initM.result ||
        m.byeValue !== initM.byeValue;
    });

    const isDirty = roundsChanged || matchesChanged;

    if (!isDirty) {
      onNext();
      return;
    }

    if (rounds.length !== nroRondas) {
      const confirmRoundsCount = window.confirm(
        `La cantidad de rondas configuradas (${rounds.length}) no coincide con la cantidad declarada en los datos generales del torneo (${nroRondas}). ¿Deseas guardar de todos modos?`
      );
      if (!confirmRoundsCount) return;
    }

    const roundsWithIncompletePlayers = [];
    const totalEnrolledIds = enrolledPlayers.map((p) => p.jug_id);

    for (const r of rounds) {
      const activePlayersInRound = new Set();
      const roundMatches = matches.filter((m) => m.par_ron_id === r.ron_id);

      for (const m of roundMatches) {
        activePlayersInRound.add(m.jug_blancas);
        if (m.jug_negras !== 'BYE') {
          activePlayersInRound.add(m.jug_negras);
        }
      }

      const missingAny = totalEnrolledIds.some((id) => !activePlayersInRound.has(id));
      if (missingAny) {
        roundsWithIncompletePlayers.push(r.ron_numero);
      }
    }

    if (roundsWithIncompletePlayers.length > 0) {
      const roundsList = roundsWithIncompletePlayers.join(', ');
      const confirmIncomplete = window.confirm(
        `En las ronda(s) [${roundsList}], no todos los jugadores inscritos tienen una partida o BYE asignado. ¿Deseas guardar de todos modos y completar los datos en otro momento?`
      );
      if (!confirmIncomplete) return;
    }

    const dtoPartidas = matches.map((m) => {
      const isBye = m.jug_negras === 'BYE';
      const {
        par_resultado_blancas,
        par_tipo_res_blancas,
        par_resultado_negras,
        par_tipo_res_negras,
      } = serializeMatchResult(m.result, isBye, m.byeValue);

      return {
        par_ron_id: m.par_ron_id,
        par_jug_blancas: m.jug_blancas,
        par_jug_negras: isBye ? null : m.jug_negras,
        par_resultado_blancas,
        par_tipo_res_blancas,
        par_resultado_negras,
        par_tipo_res_negras,
      };
    });

    const dtoRondas = rounds.map((r) => ({
      ron_id: r.ron_id,
      ron_numero: r.ron_numero,
      ron_fecha: r.ron_fecha || undefined,
    }));

    setLoading(true);
    try {
      await saveRoundsMatches(torId, { rondas: dtoRondas, partidas: dtoPartidas });
      setAlertInfo({
        open: true,
        message: 'Rondas y partidas guardadas correctamente.',
        severity: 'success',
      });
      onNext();
    } catch (error) {
      console.error('Error saving rounds/matches:', error);
      setAlertInfo({
        open: true,
        message: error.message || 'Error al guardar las rondas y partidas.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const activeRound = rounds.find((r) => r.ron_id === activeRoundId) || rounds[0];
  const activeRoundMatches = matches.filter((m) => m.par_ron_id === activeRoundId);

  return (
    <Box>
      <Typography variant="h6" sx={{ color: '#cbd5e1', mb: 2, fontWeight: 600 }}>
        Paso 3: Rondas y Partidas
      </Typography>

      {dataLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Left Column: Rounds List */}
          <Grid item xs={12} md={4}>
            <RoundsPanel
              rounds={rounds}
              activeRoundId={activeRoundId}
              onRoundSelect={setActiveRoundId}
              onRoundDateChange={handleRoundDateChange}
              onAddRound={handleAddRound}
              minHeight="300px"
              maxListHeight="250px"
              addLabel="+ Agregar Ronda"
            />
          </Grid>

          {/* Right Column: Active Round Matches */}
          <Grid item xs={12} md={8}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                background: 'rgba(15, 23, 42, 0.5)',
                borderColor: 'rgba(255, 255, 255, 0.08)',
                borderRadius: 0,
                minHeight: '300px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {activeRound ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ color: '#cbd5e1', fontWeight: 600 }}>
                      Partidas de la: <span style={{ color: '#3a6eb6' }}>Ronda {activeRound.ron_numero}</span>
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#3a6eb6', fontWeight: 'bold' }}>
                      Total: {activeRoundMatches.length} partidas
                    </Typography>
                  </Box>

                  {/* Form to Add Match */}
                  <Box sx={{ maxWidth: '680px', width: '100%', mb: 2.5 }}>
                    <Grid container spacing={1.5} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Blancas"
                          value={selectedWhite}
                          onChange={(e) => setSelectedWhite(e.target.value)}
                          InputLabelProps={{ style: { color: '#94a3b8' } }}
                          SelectProps={{ style: { color: '#cbd5e1' }, MenuProps: selectMenuProps }}
                          sx={compactInputSx}
                        >
                          <MenuItem value="">
                            <em>Selecciona un jugador...</em>
                          </MenuItem>
                          {enrolledPlayers.map((p) => (
                            <MenuItem key={p.jug_id} value={p.jug_id}>
                              {p.apellido_nombre}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        {selectedBlack === 'BYE' ? (
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Valor del BYE"
                            value={selectedByeValue}
                            onChange={(e) => setSelectedByeValue(e.target.value)}
                            InputLabelProps={{ style: { color: '#94a3b8' } }}
                            SelectProps={{ style: { color: '#cbd5e1' } }}
                            sx={{ '& .MuiOutlinedInput-root': { height: '34px', borderRadius: 0, fontSize: '0.85rem' } }}
                          >
                            <MenuItem value=""><em>Selecciona...</em></MenuItem>
                            <MenuItem value="1">1</MenuItem>
                            <MenuItem value="½">½</MenuItem>
                            <MenuItem value="0">0</MenuItem>
                          </TextField>
                        ) : (
                          <MatchResultSelect
                            value={selectedResult}
                            onChange={(e) => setSelectedResult(e.target.value)}
                            disabled={!selectedBlack}
                            placeholder="Selecciona..."
                          />
                        )}
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Negras / BYE"
                          value={selectedBlack}
                          onChange={(e) => {
                            setSelectedBlack(e.target.value);
                            if (e.target.value === 'BYE') {
                              setSelectedResult('');
                            } else {
                              setSelectedByeValue('');
                            }
                          }}
                          InputLabelProps={{ style: { color: '#94a3b8' } }}
                          SelectProps={{ style: { color: '#cbd5e1' }, MenuProps: selectMenuProps }}
                          sx={compactInputSx}
                        >
                          <MenuItem value="">
                            <em>Selecciona un jugador...</em>
                          </MenuItem>
                          <MenuItem value="BYE" sx={{ fontWeight: 'bold', color: '#3a6eb6' }}>
                            BYE
                          </MenuItem>
                          {enrolledPlayers.map((p) => (
                            <MenuItem key={p.jug_id} value={p.jug_id}>
                              {p.apellido_nombre}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12} sm={1}>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={handleAddMatch}
                          sx={{
                            height: '34px',
                            background: '#3a6eb6',
                            borderRadius: 0,
                            '&:hover': { background: '#2a5793' },
                          }}
                        >
                          +
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Matches Table */}
                  {activeRoundMatches.length === 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexGrow: 1,
                        py: 3,
                        flexDirection: 'column',
                        textAlign: 'center',
                        border: '1px dashed rgba(255, 255, 255, 0.08)',
                      }}
                    >
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        No hay partidas cargadas para esta ronda aún.
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer sx={{ border: '1px solid rgba(255, 255, 255, 0.05)', flexGrow: 1, overflowX: 'hidden' }}>
                      <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ py: 0.5, width: '38%', background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>Blancas</TableCell>
                            <TableCell align="center" sx={{ py: 0.5, width: '14%', background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>Resultado</TableCell>
                            <TableCell sx={{ py: 0.5, width: '38%', background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>Negras</TableCell>
                            <TableCell align="right" sx={{ py: 0.5, width: '10%', background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {activeRoundMatches.map((m) => (
                            <TableRow key={m.id} hover sx={{ '&:hover': { background: 'rgba(255, 255, 255, 0.02) !important' } }}>
                              <TableCell sx={{ py: 0.5, color: '#94a3b8', borderColor: 'rgba(255, 255, 255, 0.05)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {getPlayerName(m.jug_blancas)}
                              </TableCell>
                              <TableCell align="center" sx={{ py: 0.5, color: '#3a6eb6', fontWeight: 'bold', fontFamily: 'Lucida Console, monospace', borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                                {m.jug_negras === 'BYE' ? m.byeValue : m.result}
                              </TableCell>
                              <TableCell sx={{ py: 0.5, color: '#94a3b8', borderColor: 'rgba(255, 255, 255, 0.05)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {getPlayerName(m.jug_negras)}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 0.5, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteMatch(m.id)}
                                  sx={{ p: 0.25, color: '#ef4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              ) : null}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Navigation Buttons */}
      <StepNavButtons
        onBack={onBack}
        onNext={handleSaveAndNext}
        loading={loading}
      />
    </Box>
  );
}
